import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Avatar, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider,
  CircularProgress, Badge, Switch, FormControlLabel, Alert, Skeleton,
  Tabs, Tab
} from "@mui/material";
import {
  Search, Block, CheckCircle, Visibility, AttachMoney,
  Refresh, Person, Email, WorkHistory, CreditCard, Warning
} from "@mui/icons-material";
import {
  collection, onSnapshot, doc, updateDoc, query, where,
  orderBy, limit, getDocs, Timestamp
} from "firebase/firestore";
import { db } from "../firebase";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Customer {
  id: string;
  uid?: string;
  email: string;
  displayName: string;
  plan: string;
  role?: string;
  isActive: boolean;
  createdAt: any;
  totalSpent?: number;
  jobCount?: number;
  isFlagged?: boolean;
}

interface CustomerJob {
  id: string;
  category: string;
  status: string;
  createdAt: any;
  pricing?: { finalTotal?: number; estimatedTotal?: number };
}

// ─── Utils ────────────────────────────────────────────────────────────────────

function formatDate(ts: any): string {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatCurrency(v: number | undefined): string {
  if (!v) return "$0.00";
  return `$${v.toFixed(2)}`;
}

function getPlanColor(plan: string): "default" | "primary" | "success" | "warning" {
  if (plan?.toLowerCase().includes("tech")) return "primary";
  return "default";
}

function getStatusColor(status: string): string {
  switch (status) {
    case "completed": return "#22c55e";
    case "cancelled":
    case "couldnt_fix": return "#ef4444";
    case "in_progress":
    case "en_route":
    case "arrived": return "#3b82f6";
    default: return "#f59e0b";
  }
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

interface SummaryCardProps {
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  icon: React.ReactNode;
  loading?: boolean;
}

function SummaryCard({ label, value, sub, color, icon, loading }: SummaryCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
              {label}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{value}</Typography>
            )}
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ bgcolor: `${color}22`, borderRadius: 2, p: 1.5, color }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Customer Detail Panel ─────────────────────────────────────────────────────

function CustomerDetailDialog({
  customer,
  open,
  onClose,
  onBanToggle,
}: {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  onBanToggle: (cust: Customer) => void;
}) {
  const [jobs, setJobs] = useState<CustomerJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Tech Pass grant
  const [grantDays, setGrantDays] = useState(30);
  const [grantNote, setGrantNote] = useState("");
  const [granting, setGranting] = useState(false);
  const [grantSuccess, setGrantSuccess] = useState(false);

  const handleGrantTechPass = async () => {
    if (!customer) return;
    setGranting(true);
    setGrantSuccess(false);
    try {
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + grantDays);
      await updateDoc(doc(db, "users", customer.id), {
        plan: "techpass",
        techPassTrialEnd: Timestamp.fromDate(trialEnd),
        techPassSource: "admin_grant",
        techPassGrantNote: grantNote || `Granted ${grantDays} days by NeuroFlow admin`,
        techPassGrantedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setGrantSuccess(true);
      setTimeout(() => setGrantSuccess(false), 3000);
    } catch (e) {
      console.error(e);
    }
    setGranting(false);
  };

  useEffect(() => {
    if (!customer || !open) return;
    setLoadingJobs(true);
    const q = query(
      collection(db, "jobs"),
      where("customerId", "==", customer.id),
      orderBy("createdAt", "desc"),
      limit(5)
    );
    getDocs(q).then((snap) => {
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() } as CustomerJob)));
      setLoadingJobs(false);
    }).catch(() => setLoadingJobs(false));
  }, [customer, open]);

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Avatar sx={{ bgcolor: "primary.main" }}>
          {customer.displayName?.charAt(0)?.toUpperCase() || "?"}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={700}>{customer.displayName || "Unknown"}</Typography>
          <Typography variant="caption" color="text.secondary">{customer.email}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="Account Info" />
          <Tab label="Job History" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Plan</Typography>
                <Typography fontWeight={600}>
                  <Chip
                    label={customer.plan?.toUpperCase() || "FREE"}
                    size="small"
                    color={getPlanColor(customer.plan)}
                    sx={{ fontWeight: 700 }}
                  />
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography fontWeight={600} color={customer.isActive ? "success.main" : "error.main"}>
                  {customer.isActive ? "Active" : "Banned"}
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Total Jobs</Typography>
                <Typography fontWeight={700}>{customer.jobCount ?? 0}</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Total Spent</Typography>
                <Typography fontWeight={700} color="success.main">{formatCurrency(customer.totalSpent)}</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Joined</Typography>
                <Typography fontWeight={600}>{formatDate(customer.createdAt)}</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Flagged</Typography>
                <Typography fontWeight={600} color={customer.isFlagged ? "warning.main" : "text.secondary"}>
                  {customer.isFlagged ? "⚠️ Yes" : "No"}
                </Typography>
              </Box>
            </Box>

            {customer.isFlagged && (
              <Alert severity="warning" icon={<Warning />}>
                This customer has been flagged for review.
              </Alert>
            )}

            {/* ── Grant Tech Pass ─────────────────────────────── */}
            <Box sx={{
              p: 2, borderRadius: 2,
              border: "1px solid rgba(102,51,153,0.4)",
              bgcolor: "rgba(102,51,153,0.08)"
            }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: "primary.main" }}>
                🎁 Grant Tech Pass
              </Typography>

              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={grantDays}
                    label="Duration"
                    onChange={(e) => setGrantDays(Number(e.target.value))}
                  >
                    <MenuItem value={7}>7 days</MenuItem>
                    <MenuItem value={14}>14 days</MenuItem>
                    <MenuItem value={30}>30 days (1 month)</MenuItem>
                    <MenuItem value={90}>90 days (3 months)</MenuItem>
                    <MenuItem value={180}>180 days (6 months)</MenuItem>
                    <MenuItem value={365}>1 year</MenuItem>
                    <MenuItem value={3650}>Lifetime (10yr)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Note (optional)"
                  placeholder="Family, VIP, partner..."
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  sx={{ flex: 1, minWidth: 160 }}
                />

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleGrantTechPass}
                  disabled={granting}
                  sx={{
                    bgcolor: "#663399", "&:hover": { bgcolor: "#7B3FB5" },
                    whiteSpace: "nowrap", height: 40
                  }}
                >
                  {granting ? <CircularProgress size={16} color="inherit" /> : "Grant ✓"}
                </Button>
              </Box>

              {grantSuccess && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  ✅ Tech Pass granted for {grantDays} days! App will reflect this immediately.
                </Alert>
              )}

              {customer.plan?.toLowerCase().includes("tech") && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1, display: "block" }}>
                  Already on Tech Pass — granting will extend their access.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            {loadingJobs ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : jobs.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No jobs found
              </Typography>
            ) : (
              jobs.map((job) => (
                <Box
                  key={job.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                    borderBottom: "1px solid rgba(255,255,255,0.06)"
                  }}
                >
                  <Box>
                    <Typography fontWeight={600} fontSize={14}>
                      {(job.category || "service").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(job.createdAt)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontSize={13} color="success.main" fontWeight={600}>
                      {formatCurrency(job.pricing?.finalTotal ?? job.pricing?.estimatedTotal)}
                    </Typography>
                    <Chip
                      label={job.status?.replace(/_/g, " ")}
                      size="small"
                      sx={{ bgcolor: `${getStatusColor(job.status)}22`, color: getStatusColor(job.status), fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 1, px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">Close</Button>
        <Button
          onClick={() => onBanToggle(customer)}
          variant="contained"
          color={customer.isActive ? "error" : "success"}
          size="small"
          startIcon={customer.isActive ? <Block /> : <CheckCircle />}
        >
          {customer.isActive ? "Ban User" : "Unban User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Refund Dialog ─────────────────────────────────────────────────────────────

function RefundDialog({
  customer,
  open,
  onClose,
  onConfirm,
}: {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!customer) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Refund</DialogTitle>
      <DialogContent>
        <Typography>
          Issue a refund for <strong>{customer.displayName || customer.email}</strong>?
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          This will trigger a Stripe refund for their most recent completed job.
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="warning" startIcon={<AttachMoney />}>
          Issue Refund
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export function CustomersView() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState<Customer | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState("");
  const [banSuccess, setBanSuccess] = useState("");

  // Summary stats
  const [jobCounts, setJobCounts] = useState<Record<string, { count: number; spent: number }>>({});

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      // Exclude technician accounts — they live in the Techs view
      const users = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as Customer))
        .filter(u => u.role !== "technician");
      setCustomers(users);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Load job counts per customer
  useEffect(() => {
    if (customers.length === 0) return;
    getDocs(collection(db, "jobs")).then((snap) => {
      const counts: Record<string, { count: number; spent: number }> = {};
      snap.docs.forEach(d => {
        const data = d.data();
        const cid = data.customerId as string;
        const pricing = data.pricing as any;
        const total = pricing?.finalTotal ?? pricing?.estimatedTotal ?? 0;
        if (!counts[cid]) counts[cid] = { count: 0, spent: 0 };
        counts[cid].count++;
        if (data.status === "completed") counts[cid].spent += total;
      });
      setJobCounts(counts);
    });
  }, [customers.length]);

  const enriched = customers.map(c => ({
    ...c,
    jobCount: jobCounts[c.id]?.count ?? 0,
    totalSpent: jobCounts[c.id]?.spent ?? 0,
  }));

  const filtered = enriched
    .filter(c => {
      if (!search) return true;
      const s = search.toLowerCase();
      return c.displayName?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.id.includes(s);
    })
    .filter(c => filterPlan === "all" || c.plan?.toLowerCase().includes(filterPlan.toLowerCase()))
    .filter(c => {
      if (filterStatus === "all") return true;
      if (filterStatus === "active") return c.isActive !== false;
      if (filterStatus === "banned") return c.isActive === false;
      if (filterStatus === "flagged") return c.isFlagged === true;
      return true;
    });

  const techPassCount = enriched.filter(c => c.plan?.toLowerCase().includes("tech")).length;
  const activeToday = enriched.filter(c => {
    if (!c.createdAt) return false;
    const d = c.createdAt.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }).length;
  const flaggedCount = enriched.filter(c => c.isFlagged).length;

  const handleBanToggle = async (cust: Customer) => {
    await updateDoc(doc(db, "users", cust.id), { isActive: !cust.isActive });
    setBanSuccess(`${cust.isActive ? "Banned" : "Unbanned"} ${cust.displayName || cust.email}`);
    setDetailOpen(false);
    setTimeout(() => setBanSuccess(""), 3000);
  };

  const handleRefundConfirm = () => {
    // In production this would call a Firebase Function to trigger Stripe refund
    setRefundSuccess(`Refund initiated for ${refundTarget?.displayName || refundTarget?.email}`);
    setRefundOpen(false);
    setRefundTarget(null);
    setTimeout(() => setRefundSuccess(""), 4000);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>Customer Management</Typography>

      {/* Alerts */}
      {banSuccess && <Alert severity="success" sx={{ mb: 2 }}>{banSuccess}</Alert>}
      {refundSuccess && <Alert severity="info" sx={{ mb: 2 }}>{refundSuccess}</Alert>}

      {/* Summary Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2, mb: 3 }}>
        <SummaryCard
          label="Total Customers"
          value={loading ? "…" : customers.length}
          color="#6366f1"
          icon={<Person />}
          loading={loading}
        />
        <SummaryCard
          label="Joined Today"
          value={loading ? "…" : activeToday}
          sub="new signups today"
          color="#22c55e"
          icon={<CheckCircle />}
          loading={loading}
        />
        <SummaryCard
          label="Tech Pass"
          value={loading ? "…" : techPassCount}
          sub="active subscribers"
          color="#8b5cf6"
          icon={<CreditCard />}
          loading={loading}
        />
        <SummaryCard
          label="Flagged"
          value={loading ? "…" : flaggedCount}
          sub="need review"
          color="#f59e0b"
          icon={<Warning />}
          loading={loading}
        />
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search by name, email, or ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} /> }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Plan</InputLabel>
              <Select value={filterPlan} label="Plan" onChange={e => setFilterPlan(e.target.value)}>
                <MenuItem value="all">All Plans</MenuItem>
                <MenuItem value="tech">Tech Pass</MenuItem>
                <MenuItem value="free">Free</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="banned">Banned</MenuItem>
                <MenuItem value="flagged">Flagged</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
              {filtered.length} of {customers.length} customers
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Customer</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Email</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Plan</Typography></TableCell>
              <TableCell align="center"><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Jobs</Typography></TableCell>
              <TableCell align="right"><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Total Spent</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Joined</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Status</Typography></TableCell>
              <TableCell align="right"><Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No customers found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((cust) => (
                <TableRow
                  key={cust.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    opacity: cust.isActive === false ? 0.5 : 1,
                    "&:hover": { bgcolor: "rgba(99,102,241,0.04)" }
                  }}
                  onClick={() => { setSelectedCustomer(cust); setDetailOpen(true); }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "primary.main" }}>
                        {cust.displayName?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>
                          {cust.displayName || "—"}
                          {cust.isFlagged && " ⚠️"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: 10 }}>
                          {cust.id.slice(0, 8)}…
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13}>{cust.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cust.plan?.toLowerCase().includes("tech") ? "TECH PASS" : "FREE"}
                      size="small"
                      color={getPlanColor(cust.plan)}
                      sx={{ fontWeight: 700, fontSize: 10 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontSize={13} fontWeight={600}>{cust.jobCount}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontSize={13} fontWeight={600} color="success.main">
                      {formatCurrency(cust.totalSpent)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={12} color="text.secondary">{formatDate(cust.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cust.isActive === false ? "Banned" : "Active"}
                      size="small"
                      color={cust.isActive === false ? "error" : "success"}
                      sx={{ fontWeight: 600, fontSize: 10 }}
                    />
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => { setSelectedCustomer(cust); setDetailOpen(true); }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Issue Refund">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => { setRefundTarget(cust); setRefundOpen(true); }}
                        >
                          <AttachMoney fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={cust.isActive === false ? "Unban" : "Ban"}>
                        <IconButton
                          size="small"
                          color={cust.isActive === false ? "success" : "error"}
                          onClick={() => handleBanToggle(cust)}
                        >
                          {cust.isActive === false ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogs */}
      <CustomerDetailDialog
        customer={selectedCustomer}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onBanToggle={handleBanToggle}
      />
      <RefundDialog
        customer={refundTarget}
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        onConfirm={handleRefundConfirm}
      />
    </Box>
  );
}
